import {
  type ImageViewerRolodexOverride,
  useImageViewerLogic,
} from 'features/knowledgeDocumentViewer/components/viewer/image/hooks/useImageViewerLogic'
import { RolodexPagePreloader } from 'features/knowledgeDocumentViewer/components/viewer/image/hooks/useRolodexPagePreloader'
import type { SearchHighlightData } from 'features/knowledgeDocumentViewer/components/viewer/search/SearchHighlightOverlay'
import { DocumentMinimap } from 'features/knowledgeDocumentViewer/components/viewer/shared/DocumentMinimap'
import { useAliasTooltip } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useAliasTooltip'
import { useBoxHighlight } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useBoxHighlight'
import { useBoxStyles } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useBoxStyles'
import { useMinimapPreference } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useMinimapPreference'
import { SubjectTimeSeriesTooltip } from 'features/knowledgeDocumentViewer/components/viewer/shared/SubjectTimeSeriesTooltip'
import {
  type DocumentViewerHandle,
  type FrameTarget,
  type TransformState,
  type ViewerCommonProps,
} from 'features/knowledgeDocumentViewer/components/viewer/shared/types'
import { ViewerOverlaySvg } from 'features/knowledgeDocumentViewer/components/viewer/shared/ViewerOverlaySvg'
import { ZoomControls } from 'features/knowledgeDocumentViewer/components/viewer/shared/ZoomControls'
import { useCantileverPageHostsOptional } from 'features/knowledgeDocumentViewer/contexts/CantileverPageHostsContext'
import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { useTimeSeriesLinkContext } from 'features/knowledgeDocumentViewer/contexts/TimeSeriesLinkContext'
import { DocumentViewerPage } from 'features/knowledgeDocumentViewer/types'
import { resolvePageImageFileRefDual } from 'features/knowledgeDocumentViewer/utils/documentPageImages'
import { useRolodexSignedURLs } from 'features/rolodex'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { ContinuousPageImage } from './ContinuousPageImage'
import {
  PageListWrapper,
  PageOverlayContainer,
  PageSlot,
  PageSpacer,
  ScrollContainer,
} from './continuousScrollStyles'
import { useContinuousScrollSync } from './hooks/useContinuousScrollSync'
import { MAX_ZOOM, useContinuousZoom } from './hooks/useContinuousZoom'
import { useDragToScroll } from './hooks/useDragToScroll'
import { useRafScrollPosSync } from './hooks/useRafScrollPosSync'
import { useVirtualPageWindow } from './hooks/useVirtualPageWindow'
import { buildPageLayoutMetrics } from './pageLayoutMetrics'

const PAGE_GAP = 8
const SCROLL_PADDING = 16

type ContinuousScrollViewerProps = ViewerCommonProps & {
  documentId: string
  pageNumber: number
  pdfUrl: string
  scale: number
  totalPages: number
  onPageChange: (page: number) => void
  isActiveTab: boolean
  searchHighlights?: SearchHighlightData
  frameTarget?: FrameTarget
  onFrameApplied?: () => void
}

export const ContinuousScrollViewer = forwardRef<
  DocumentViewerHandle,
  ContinuousScrollViewerProps
>(function ContinuousScrollViewer(props, ref) {
  const {
    documentId,
    pageNumber,
    pdfUrl,
    scale,
    totalPages,
    onPageChange,
    isActiveTab,
    highlightBoxIds,
    onBoxClick,
    onBoxAuxClick,
    onBoxContextMenu,
    onBoxHover,
    onAliasClick,
    onDocReferenceClick,
    onDocReferenceHover,
    actionMode,
    searchHighlights,
    frameTarget,
    onFrameApplied,
    showTooltips = false,
  } = props

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const [pageDimsMap, setPageDimsMap] = useState<
    Map<number, { w: number; h: number }>
  >(() => new Map())
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 })

  useEffect(() => {
    const el = scrollContainerRef.current
    if (!el) return
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry)
        setContainerSize({
          w: entry.contentRect.width,
          h: entry.contentRect.height,
        })
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const referenceDims = useMemo<{ w: number; h: number } | null>(() => {
    if (pageDimsMap.size === 0) return null
    return pageDimsMap.get(1) ?? pageDimsMap.values().next().value ?? null
  }, [pageDimsMap])

  const {
    effectiveZoom,
    minZoom,
    zoomIn,
    zoomOut,
    resetZoom,
    setZoomLevel,
    isAtMinZoom,
    isAtMaxZoom,
  } = useContinuousZoom(
    scrollContainerRef,
    referenceDims,
    containerSize,
    SCROLL_PADDING
  )

  useDragToScroll(scrollContainerRef)

  const displayWidth = useMemo(() => {
    if (!referenceDims || effectiveZoom === 0) return 0
    return referenceDims.w * effectiveZoom
  }, [referenceDims, effectiveZoom])

  const getPageDisplayHeight = useCallback(
    (pageNum: number): number => {
      if (displayWidth === 0) return 0
      const dims = pageDimsMap.get(pageNum)
      if (dims) return displayWidth * (dims.h / dims.w)
      if (referenceDims)
        return displayWidth * (referenceDims.h / referenceDims.w)
      return 0
    },
    [displayWidth, pageDimsMap, referenceDims]
  )

  const onFrameAppliedRef = useRef(onFrameApplied)
  onFrameAppliedRef.current = onFrameApplied

  // Scroll so the frame target's region is centered in the viewport.
  const applyFrameScroll = useCallback(
    (target: FrameTarget): boolean => {
      const container = scrollContainerRef.current
      if (!container) return false
      const slot = container.querySelector<HTMLElement>(
        `[data-page="${target.pageNumber}"]`
      )
      if (!slot || slot.offsetWidth === 0 || slot.offsetHeight === 0) {
        return false
      }
      const { bbox } = target
      const centerX =
        slot.offsetLeft + (bbox.left + bbox.width / 2) * slot.offsetWidth
      const centerY =
        slot.offsetTop + (bbox.top + bbox.height / 2) * slot.offsetHeight
      container.scrollLeft = Math.max(0, centerX - container.clientWidth / 2)
      container.scrollTop = Math.max(0, centerY - container.clientHeight / 2)
      onFrameAppliedRef.current?.()
      return true
    },
    [scrollContainerRef]
  )

  const pendingFrameScrollRef = useRef<FrameTarget | null>(null)
  const lastFramedTargetRef = useRef<FrameTarget | null>(null)

  const { currentPage, loadedRange, handleExternalPageChange } =
    useContinuousScrollSync(
      scrollContainerRef,
      totalPages,
      getPageDisplayHeight,
      PAGE_GAP,
      pageNumber,
      onPageChange
    )

  useEffect(() => {
    // When the page change carries a frame target for that page and the
    // frame already positioned the viewport inside it (order depends on
    // whether the page's image dims were preloaded), a page-top scroll
    // would stomp the frame — skip it; the scroll listener still syncs
    // currentPage/loadedRange from the framed position.
    if (
      frameTarget &&
      frameTarget.pageNumber === pageNumber &&
      lastFramedTargetRef.current === frameTarget
    ) {
      return
    }
    handleExternalPageChange(pageNumber)
  }, [pageNumber, handleExternalPageChange, frameTarget])

  // Complete a pending frame once the layout reflects the zoom picked below.
  // Runs after useContinuousZoom's anchor effect (hook order), so the frame
  // scroll is the one that lands.
  useLayoutEffect(() => {
    const pending = pendingFrameScrollRef.current
    if (!pending) return
    if (applyFrameScroll(pending)) {
      pendingFrameScrollRef.current = null
      lastFramedTargetRef.current = pending
    }
  }, [effectiveZoom, displayWidth, loadedRange, applyFrameScroll])

  // Frame a cited region: pick a zoom where the region's larger dimension
  // fills ~30% of the viewport (clamped to the zoom band), then center it.
  // Waits for the target page's measured dimensions — retried as pageDimsMap
  // fills in, since navigation to the page (above) triggers its image load.
  useEffect(() => {
    if (!frameTarget || lastFramedTargetRef.current === frameTarget) return
    const container = scrollContainerRef.current
    if (!container || !referenceDims || effectiveZoom === 0) return
    const dims = pageDimsMap.get(frameTarget.pageNumber)
    if (!dims) return

    const { bbox } = frameTarget
    // Every page slot renders at displayWidth = referenceDims.w * zoom, with
    // height following the page's own aspect ratio.
    const regionDisplayW = Math.max(bbox.width * referenceDims.w, 1)
    const regionDisplayH = Math.max(
      bbox.height * referenceDims.w * (dims.h / dims.w),
      1
    )
    const ideal = Math.min(
      (0.3 * container.clientWidth) / regionDisplayW,
      (0.3 * container.clientHeight) / regionDisplayH
    )
    const targetZoom = Math.min(MAX_ZOOM, Math.max(minZoom, ideal))

    if (Math.abs(targetZoom - effectiveZoom) < 0.001) {
      pendingFrameScrollRef.current = frameTarget
      if (applyFrameScroll(frameTarget)) {
        pendingFrameScrollRef.current = null
        lastFramedTargetRef.current = frameTarget
      }
      return
    }

    pendingFrameScrollRef.current = frameTarget
    setZoomLevel(targetZoom)
  }, [
    frameTarget,
    referenceDims,
    effectiveZoom,
    pageDimsMap,
    minZoom,
    setZoomLevel,
    applyFrameScroll,
  ])

  const handlePageDimensionsKnown = useCallback(
    (pageNum: number, w: number, h: number) => {
      if (w <= 0 || h <= 0) return
      setPageDimsMap((prev) => {
        if (prev.has(pageNum)) return prev
        const next = new Map(prev)
        next.set(pageNum, { w, h })
        return next
      })
    },
    []
  )

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return
      }
      const container = scrollContainerRef.current
      if (!container) return
      if (e.code === 'Space') {
        e.preventDefault()
        resetZoom()
      } else if (e.key === '=' || e.key === '+') {
        e.preventDefault()
        zoomIn()
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault()
        zoomOut()
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        container.scrollBy({ top: -120, behavior: 'smooth' })
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        container.scrollBy({ top: 120, behavior: 'smooth' })
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [resetZoom, zoomIn, zoomOut])

  // Overlay state for current page
  const {
    hoverId,
    activeTabId,
    currentSelectedBoxIds,
    updateDocumentHistoryItem,
    handleMouseEnter: ctxMouseEnter,
    handleMouseLeave: ctxMouseLeave,
  } = useKnowledgeDocumentUXContext()
  const [internalHighlightedBoxIds, setInternalHighlightedBoxIds] =
    useBoxHighlight(highlightBoxIds)
  const { fillFor, strokeFor } = useBoxStyles(
    hoverId,
    internalHighlightedBoxIds,
    actionMode
  )
  const { hasTimeSeriesData } = useTimeSeriesLinkContext()

  const pageHosts = useCantileverPageHostsOptional()
  const currentPageHostId = pageHosts?.getCantileverPageHostId(currentPage)
  const currentPageRolodexQuery = useMemo(
    () =>
      resolvePageImageFileRefDual(
        documentId,
        currentPage,
        'hiRes',
        currentPageHostId
      ),
    [currentPage, currentPageHostId, documentId]
  )
  const { url: currentPageUrl } = useRolodexSignedURLs(currentPageRolodexQuery)
  const currentPageOverride = useMemo<ImageViewerRolodexOverride | undefined>(
    () =>
      currentPageUrl
        ? { imageUrl: currentPageUrl, isLoading: false }
        : undefined,
    [currentPageUrl]
  )
  const { boxes: currentPageBoxes, dims: currentPageDims } =
    useImageViewerLogic(
      pdfUrl || undefined,
      currentPage,
      scale,
      DocumentViewerPage.LEFT_PAGE,
      totalPages,
      isActiveTab,
      currentPageOverride
    )

  const handleBackgroundClick = useCallback(() => {
    if (currentSelectedBoxIds.length > 0) {
      updateDocumentHistoryItem(activeTabId, { selectedBoxIds: [] })
    }
  }, [activeTabId, currentSelectedBoxIds, updateDocumentHistoryItem])

  const handleBoxMouseEnter = useCallback(
    (_box: React.ComponentProps<typeof ViewerOverlaySvg>['boxes'][number]) => {
      ctxMouseEnter(_box)
      onBoxHover?.(_box)
    },
    [ctxMouseEnter, onBoxHover]
  )

  const handleBoxMouseLeave = useCallback(() => {
    ctxMouseLeave()
    onBoxHover?.(null)
  }, [ctxMouseLeave, onBoxHover])

  const noopSvgMouse = useCallback(() => {}, [])

  const pageLayoutMetrics = useMemo(
    () => buildPageLayoutMetrics(totalPages, getPageDisplayHeight, PAGE_GAP),
    [getPageDisplayHeight, totalPages]
  )

  const virtualPageWindow = useVirtualPageWindow(
    loadedRange,
    totalPages,
    PAGE_GAP,
    pageLayoutMetrics
  )

  const currentPageTop = useMemo(
    () => pageLayoutMetrics.tops[currentPage - 1] ?? 0,
    [currentPage, pageLayoutMetrics]
  )

  const fitComplete = referenceDims !== null && effectiveZoom > 0

  const { showMinimap, toggleMinimap } = useMinimapPreference({
    isZoomed: effectiveZoom > 0 && !isAtMinZoom,
    isContentReady: currentPageUrl !== undefined && referenceDims !== null,
  })
  const scrollPos = useRafScrollPosSync(scrollContainerRef, showMinimap)

  const currentPageNaturalDims = useMemo(
    () => pageDimsMap.get(currentPage) ?? referenceDims,
    [pageDimsMap, currentPage, referenceDims]
  )

  const minimapTransformState = useMemo<TransformState>(() => {
    if (!referenceDims || !scrollContainerRef.current) {
      return { scale: 1, positionX: 0, positionY: 0 }
    }
    const pageScale = displayWidth / referenceDims.w
    return {
      scale: pageScale,
      positionX: -scrollPos.left,
      positionY: -(scrollPos.top - currentPageTop),
    }
  }, [referenceDims, displayWidth, currentPageTop, scrollPos])

  const handleMinimapNavigate = useCallback(
    (posX: number, posY: number) => {
      const container = scrollContainerRef.current
      if (!container || !referenceDims) return
      container.scrollLeft = -posX
      container.scrollTop = currentPageTop + -posY
    },
    [referenceDims, currentPageTop]
  )

  // Preload pages around visible range
  const preloadPages = useMemo(() => {
    const pages: number[] = []
    for (
      let p = Math.max(1, loadedRange.start - 1);
      p <= Math.min(totalPages, loadedRange.end + 1);
      p++
    ) {
      if (p < loadedRange.start || p > loadedRange.end) {
        pages.push(p)
      }
    }
    return pages
  }, [loadedRange, totalPages])

  useImperativeHandle(
    ref,
    () => ({
      zoomToBox: () => {},
      panToRegion: () => {},
      setHighlightedBoxIds: setInternalHighlightedBoxIds,
      resetZoom,
      zoomIn,
      zoomOut,
      setTransform: () => {},
      getViewportInfo: () =>
        scrollContainerRef.current
          ? {
              wrapperWidth: scrollContainerRef.current.clientWidth,
              wrapperHeight: scrollContainerRef.current.clientHeight,
            }
          : null,
      getImageNaturalDimensions: () => referenceDims ?? { w: 0, h: 0 },
    }),
    [referenceDims, resetZoom, zoomIn, zoomOut, setInternalHighlightedBoxIds]
  )

  const { handleAliasHover, aliasTooltipProps } = useAliasTooltip({
    showTooltips,
    containerRef: scrollContainerRef,
  })

  const { aliasHighlights } = useDocumentTabDataContext()
  const hasSearchHighlights =
    searchHighlights !== undefined && searchHighlights.matches.length > 0
  const hasAliasHighlights = aliasHighlights.some(
    (highlight) => highlight.page === pageNumber
  )
  const overlayReady =
    currentPageDims !== null &&
    currentPageDims.w > 0 &&
    (currentPageBoxes.length > 0 ||
      hasSearchHighlights ||
      hasAliasHighlights) &&
    currentPage === pageNumber

  return (
    <>
      <ScrollContainer ref={scrollContainerRef} $opacity={fitComplete ? 1 : 0}>
        <PageListWrapper $gap={PAGE_GAP} $padding={SCROLL_PADDING}>
          {virtualPageWindow.topSpacerHeight > 0 && (
            <PageSpacer
              $width={displayWidth}
              $height={virtualPageWindow.topSpacerHeight}
            />
          )}
          {virtualPageWindow.pages.map((pageNum) => (
            <PageSlot
              key={pageNum}
              data-page={pageNum}
              $width={displayWidth}
              $height={pageLayoutMetrics.heights[pageNum - 1] ?? 0}>
              <ContinuousPageImage
                documentId={documentId}
                pageNumber={pageNum}
                pdfUrl={pdfUrl || undefined}
                totalPages={totalPages}
                onDimensionsKnown={handlePageDimensionsKnown}
              />
              {pageNum === currentPage && overlayReady && (
                <PageOverlayContainer>
                  <ViewerOverlaySvg
                    actionMode={actionMode}
                    boxes={currentPageBoxes}
                    fillFor={fillFor}
                    hasTimeSeriesData={hasTimeSeriesData}
                    imageNatural={currentPageDims}
                    isDragging={false}
                    isMultipage={false}
                    onBackgroundClick={handleBackgroundClick}
                    onBoxAuxClick={onBoxAuxClick}
                    onBoxClick={onBoxClick}
                    onBoxContextMenu={onBoxContextMenu}
                    onBoxMouseEnter={handleBoxMouseEnter}
                    onBoxMouseLeave={handleBoxMouseLeave}
                    onAliasClick={onAliasClick}
                    onAliasHover={handleAliasHover}
                    onDocReferenceClick={onDocReferenceClick}
                    onDocReferenceHover={onDocReferenceHover}
                    onMouseDown={noopSvgMouse}
                    onMouseMove={noopSvgMouse}
                    onMouseUp={noopSvgMouse}
                    searchHighlights={searchHighlights}
                    selectionBox={null}
                    strokeFor={strokeFor}
                    svgRef={svgRef}
                  />
                </PageOverlayContainer>
              )}
            </PageSlot>
          ))}
          {virtualPageWindow.bottomSpacerHeight > 0 && (
            <PageSpacer
              $width={displayWidth}
              $height={virtualPageWindow.bottomSpacerHeight}
            />
          )}
        </PageListWrapper>
      </ScrollContainer>
      {aliasTooltipProps && <SubjectTimeSeriesTooltip {...aliasTooltipProps} />}
      <ZoomControls
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onResetZoom={resetZoom}
        onToggleMinimap={toggleMinimap}
        isMinimapVisible={showMinimap}
        disabled={!fitComplete}
        zoomOutDisabled={isAtMinZoom}
        zoomInDisabled={isAtMaxZoom}
      />
      {showMinimap && currentPageUrl && currentPageNaturalDims && (
        <DocumentMinimap
          imageUrls={[currentPageUrl]}
          transformState={minimapTransformState}
          imageNaturalDimensions={currentPageNaturalDims}
          wrapperDimensions={containerSize}
          onNavigate={handleMinimapNavigate}
          isMultipage={false}
        />
      )}
      {preloadPages.map((page) => (
        <RolodexPagePreloader key={page} documentId={documentId} page={page} />
      ))}
    </>
  )
})
